import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Paper,
  Stack,
  TextField,
  IconButton,
  CircularProgress,
  Container,
  Select,
  MenuItem,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import AdminDashboard from "../../Admin/Dashboard/Dashboard";
import { BASE_URL } from "../../Api/ApiUrls";
import BankActionPropertyModal from "./BankActionPropertyModal";

const BankActionProperties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [selectedRow, setSelectedRow] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/auction-property/`);

        const formatted = (res.data || []).map((item) => ({
          id: item.bankprop_id,
          auction_id: item.auction_id,
          bank_name: item.bank_name || "-",
          property_type: item.property_type || "-",
          action_type: item.action_type || "-",
          location: item.location || "-",
          city_town: item.city_town || "-",
          area_town: item.area_town || "-",
          area: item.area || "-",
          units: item.units || "-",
          possession: item.possession || "-",
          reserve_price: item.reserve_price || "-",
          possession_status: item.possession_status || "-",
          emd_amount: item.emd_amount || "-",
          bid_increment: item.bid_increment || "-",
          emd_submission: item.emd_submission
            ? new Date(item.emd_submission).toLocaleString()
            : "-",
          auction_start_datetime: item.auction_start_datetime,
          auction_end_datetime: item.auction_end_datetime,
          bank_contact_details: item.bank_contact_details,
          download_auction_file: item.download_auction_file,
          description: item.description,
          created_at: new Date(item.created_at).toLocaleString(),
          updated_at: new Date(item.updated_at).toLocaleString(),
          status: item.status || "Pending",
        }));

        setProperties(formatted);
      } catch (error) {
        console.error("Error fetching auction properties:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  const handleStatusChange = async (id, newStatus) => {
    try {
      setProperties((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, status: newStatus } : item
        )
      );

      // Later PUT API will be added
      await axios.put(`${BASE_URL}/auction-property/${id}/`, { status: newStatus });
alert("Status updated successfully!");
      console.log("Status updated locally:", newStatus);
    } catch (error) {
      console.error("Failed to update status:", error);
      alert("Failed to update status. Please try again.");
    }
  };

  const handleSearch = (event) => setSearch(event.target.value);

  const filtered = properties.filter(
    (item) =>
      item.bank_name.toLowerCase().includes(search.toLowerCase()) ||
      item.city_town.toLowerCase().includes(search.toLowerCase()) ||
      item.property_type.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this property?"))
      return;

    try {
      await axios.delete(`${BASE_URL}/auction-property/${id}/`);
      setProperties((prev) => prev.filter((p) => p.id !== id));
      alert("Deleted successfully!");
    } catch (err) {
      console.error("Error deleting:", err);
    }
  };

  const handleView = (item) => {
    setSelectedRow(item);
    setOpenModal(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      <AdminDashboard />

      <Box
        sx={{
          maxWidth: 1500,
          mx: "auto",
          p: 1,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: "#fff",
        }}
      >
        <Typography
          variant="h4"
          sx={{ textAlign: "center", color: "primary.main", mb: 2 }}
        >
          Bank Auction Properties Table
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <TextField
            label="Search Bank / City / Type"
            size="small"
            value={search}
            onChange={handleSearch}
            sx={{ width: 300 }}
          />
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  "ID",
                  "Bank Name",
                  "Property Type",
                  "City / Town",
                  "Area",
                  "Reserve Price",
                  "EMD Amount",
                  "Possession",
                  "Status",
                  "Actions",
                ].map((head) => (
                  <TableCell
                    key={head}
                    sx={{
                      backgroundColor: "#1976d2",
                      color: "white",
                      textAlign: "center",
                      fontSize: 15,
                      fontWeight: "bold",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell sx={{ textAlign: "center" }}>
                    {index + 1}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {item.bank_name}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {item.property_type}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {item.city_town}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {item.area}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    ₹{item.reserve_price}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    ₹{item.emd_amount}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {item.possession}
                  </TableCell>

                  {/* STATUS DROPDOWN */}
                  <TableCell sx={{ textAlign: "center" }}>
                    <Select
                      size="small"
                      value={item.status}
                      onChange={(e) =>
                        handleStatusChange(item.id, e.target.value)
                      }
                    >
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Approved">Approved</MenuItem>
                      <MenuItem value="Rejected">Rejected</MenuItem>
                    </Select>
                  </TableCell>

                  {/* ACTIONS */}
                  <TableCell sx={{ textAlign: "center" }}>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <IconButton color="info" onClick={() => handleView(item)}>
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(item.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* FULL DETAILS MODAL */}
        <BankActionPropertyModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          data={selectedRow}
        />
      </Box>
    </Container>
  );
};

export default BankActionProperties;
